import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, Settings, X, BarChart3, LogOut } from 'lucide-react';
import TopTabNav from './TopTabNav';
import { useBundleMonitor } from '../../hooks/useBundleMonitor';
import { useAuth } from '../../contexts/AuthContext';

const TopHeader: React.FC = () => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const today = new Date();
  const dateStr = today.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  const location = useLocation();
  const navigate = useNavigate();
  const { analyzeNow, isEnabled } = useBundleMonitor();
  const { logout } = useAuth();

  const handleBundleAnalysis = async () => {
    setAnalyzing(true);
    try {
      await analyzeNow();
    } finally {
      setAnalyzing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  return (
    <header className="w-full flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 sticky top-0 z-30">
      <div className="flex items-center gap-2">
        <button
          className="flex items-center gap-2 focus:outline-none hover:opacity-80 transition-opacity cursor-pointer"
          onClick={() => {
            navigate('/');
            setTimeout(() => window.scrollTo(0, 0), 100);
          }}
          aria-label="Navigate to Homepage"
        >
          <span className="text-xl font-bold text-gray-900">ThinkMate</span>
        </button>
      </div>
      {/* Center: Date on mobile, Tabs on desktop for /dashboard and /entries */}
      <div className="flex-1 flex justify-center">
        {/* Mobile: show date */}
        <span className="text-base font-semibold text-gray-700 md:hidden">{dateStr}</span>
        {/* Desktop: show tabs on /dashboard, /analytics and /entries */}
        {(location.pathname === '/dashboard' || location.pathname === '/analytics' || location.pathname === '/entries') && (
          <div className="hidden md:block">
            <TopTabNav />
          </div>
        )}
      </div>
      <div className="flex items-center gap-3">
        {/* Desktop: Settings in hamburger menu */}
        <div className="hidden md:block relative">
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Menu"
          >
            {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
          </button>
          {showMobileMenu && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 py-4 z-50 max-h-96 overflow-y-auto">
              <div className="px-4 pb-3 border-b border-gray-100">
                <div className="flex items-center gap-2 text-rose-600 mb-2">
                  <Settings size={20} />
                  <h3 className="font-semibold">Settings</h3>
                </div>
              </div>
              
              {/* Analytics & Insights */}
              <div className="px-4 py-3 border-b border-gray-100">
                <h4 className="font-medium text-gray-700 mb-2">Analytics & Insights</h4>
                <p className="text-gray-600 text-xs mb-3">
                  View detailed analytics about your momentum, check-ins, and progress patterns.
                </p>
                <button 
                  onClick={() => {
                    navigate('/analytics');
                    setShowMobileMenu(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-medium text-xs hover:from-blue-600 hover:to-purple-700 transition-all"
                >
                  <BarChart3 size={14} />
                  View Analytics Dashboard
                </button>
              </div>
              
              {/* Preferences */}
              <div className="px-4 py-3 border-b border-gray-100">
                <h4 className="font-medium text-gray-700 mb-3">Preferences</h4>
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-gray-600 font-medium text-xs">Preferred Check-In Time</label>
                    <input type="time" className="border rounded-lg px-2 py-1 text-gray-700 text-xs" defaultValue="08:00" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-gray-600 font-medium text-xs">Email Notifications</label>
                    <select className="border rounded-lg px-2 py-1 text-gray-700 text-xs">
                      <option>Enabled</option>
                      <option>Disabled</option>
                    </select>
                  </div>
                </div>
              </div>
              
              {/* Development Tools */}
              {isEnabled && (
                <div className="px-4 py-3">
                  <h4 className="font-medium text-gray-700 mb-2">Development Tools</h4>
                  <p className="text-gray-600 text-xs mb-3">
                    Monitor bundle size and performance metrics.
                  </p>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={handleBundleAnalysis}
                      disabled={analyzing}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium text-xs hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                      {analyzing ? 'Analyzing...' : 'Analyze Bundle'}
                    </button>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium text-xs hover:bg-gray-200 transition-colors"
                    >
                      <LogOut size={14} />
                      Log Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopHeader; 